import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, BadgeCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLE_REVIEWS = [
  {
    name: "Sem (12)",
    city: "Utrecht",
    product: "Hydro Pulse X-1",
    rating: 5,
    text: "Ziek ding. Binnen een week had mijn hele vriendengroep er ook één. De orbs spatten perfect uiteen.",
  },
  {
    name: "Familie Jansen",
    city: "Breda",
    product: "Nano Splosher",
    rating: 5,
    text: "Cadeau voor onze zoon van 9. Veilig, zachte orbs en hij rent al twee weken non-stop door de tuin.",
  },
  {
    name: "Noor (16)",
    city: "Antwerpen",
    product: "Apex Viper Neon",
    rating: 5,
    text: "30 meter bereik is geen grap. Win elke battle in het park. De dubbele magazijnen zijn goud waard.",
  },
  {
    name: "Daan",
    city: "Rotterdam",
    product: "Ghost Commando",
    rating: 4,
    text: "Avondbattles zijn legendarisch met dit ding. Fluisterstil én hij ziet er vet uit. Minpunt: ik wil er nog één.",
  },
  {
    name: "Lisa",
    city: "Amersfoort",
    product: "Mystery Pack",
    rating: 5,
    text: "Elke maand een verrassing op de mat. Mijn zoons tellen de dagen af. Echt topkwaliteit gear.",
  },
  {
    name: "Thijs (14)",
    city: "Groningen",
    product: "Hydro Pulse X-1",
    rating: 5,
    text: "Besteld om 22:45, volgende ochtend al in huis. En 10.000 orbs gratis erbij. Niet normaal.",
  },
];

const COLORS = ["#00FF66", "#00E5FF", "#FF087B"];

const Stars = ({ rating }) => (
  <div className="flex gap-0.5" data-testid="review-stars">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i <= rating ? "text-[#00FF66] fill-[#00FF66]" : "text-slate-700"}`}
      />
    ))}
  </div>
);

export const Reviews = () => {
  const [realReviews, setRealReviews] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then((res) => setRealReviews(res.data))
      .catch(() => setRealReviews([]));
  }, []);

  const hasReal = realReviews && realReviews.length > 0;
  const reviews = hasReal
    ? realReviews.map((r, i) => ({ ...r, city: null, color: COLORS[i % 3], real: true }))
    : SAMPLE_REVIEWS.map((r, i) => ({ ...r, color: COLORS[i % 3], real: false }));
  const avg = hasReal
    ? (realReviews.reduce((s, r) => s + r.rating, 0) / realReviews.length).toFixed(1)
    : "4.9";

  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 sm:py-28 border-t border-slate-800/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-14 flex flex-col sm:flex-row sm:items-end justify-between gap-6"
        >
          <div>
            <p className="font-mono-brand text-xs uppercase tracking-widest text-[#00FF66] mb-3">Reviews</p>
            <h2 className="font-display text-5xl sm:text-6xl uppercase tracking-wide text-white leading-none">
              {hasReal ? (
                realReviews.length === 1 ? (
                  <><span className="text-gradient-splat">1 splatter</span> ging je voor</>
                ) : (
                  <><span className="text-gradient-splat">{realReviews.length} splatters</span> gingen je voor</>
                )
              ) : (
                <>2.300+ <span className="text-gradient-splat">splatters</span> gingen je voor</>
              )}
            </h2>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Stars rating={5} />
            <span className="text-sm text-slate-400 font-bold" data-testid="reviews-average">{avg}/5 gemiddeld</span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <motion.figure
              key={r.order_id || r.name + i}
              data-testid={`review-card-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
              className="bg-slate-900/60 border border-slate-800 rounded-3xl p-7 hover:border-slate-600 transition-colors flex flex-col"
            >
              <Stars rating={r.rating} />
              <blockquote className="mt-4 text-sm text-slate-300 leading-relaxed flex-1">“{r.text}”</blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-extrabold text-slate-950 text-sm shrink-0"
                  style={{ backgroundColor: r.color }}
                >
                  {r.name.charAt(0)}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-white">
                    {r.name}{r.city && <span className="text-slate-500 font-normal"> • {r.city}</span>}
                  </p>
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <BadgeCheck className="w-3.5 h-3.5 text-[#00E5FF]" />
                    Geverifieerde aankoop{r.product ? ` — ${r.product}` : ""}
                  </p>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};
